import React, { createContext, useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import {
  IWhatsAppAuthState,
  IUserProfile,
  ICompanyOutlet,
  IWhatsAppServiceInfo,
  initialWhatsAppAuthState,
  defaultUserProfile,
  defaultCompanyOutlet,
} from "./WhatsAppAuth.types";
import { talkvitApi } from "../../../../api/talkvitAPI";
import { WHATSAPP_SERVICE_CONFIG } from "../../constants/whatsapp.constants";
import { decodeWhatsAppToken, IWhatsAppTokenClaims } from "./decodeToken";

// ─── Not Configured Screen ───────────────────────────────────────

const NotConfiguredScreen: React.FC<{ error: string | null }> = ({ error }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="80vh"
    textAlign="center"
    p={3}
  >
    <ErrorOutline sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
    <Typography variant="h5" fontWeight={600} color="text.secondary" mb={1}>
      WhatsApp Not Configured
    </Typography>
    <Typography variant="body1" color="text.secondary" maxWidth={400}>
      {error || "User profile or company outlet could not be loaded. Please contact your administrator."}
    </Typography>
  </Box>
);

// ─── Context ─────────────────────────────────────────────────────

const WhatsAppAuthContext = createContext<IWhatsAppAuthState>(initialWhatsAppAuthState);

// ─── Helper: extract whatsapp service from user settings ─────────

const getWhatsappServiceFromSettings = (settings: any[]): IWhatsAppServiceInfo => {
  if (!settings || !Array.isArray(settings)) {
    return { isMultiple: false, serviceProviders: [], selectedService: null, whatsappNumber: "" };
  }
  const platformsSetting = settings.find((s) => s.env_key === WHATSAPP_SERVICE_CONFIG.CURRENT_WHATSAPP_PLATFORMS);
  let validPlatforms: string[] = [];
  try {
    validPlatforms = platformsSetting ? JSON.parse(platformsSetting.setting_value) : [];
  } catch { validPlatforms = []; }

  if (validPlatforms.length > 0) {
    const selectedSetting = settings.find((s) => s.env_key === WHATSAPP_SERVICE_CONFIG.CURRENT_SELECTED_WHATSAPP_PLATFORM);
    const numberSetting = settings.find((s) => s.env_key === WHATSAPP_SERVICE_CONFIG.BAILEY_WHATSAPP_NUMBER);
    return {
      isMultiple: validPlatforms.length > 1,
      serviceProviders: validPlatforms,
      selectedService: selectedSetting ? selectedSetting.setting_value : validPlatforms[0],
      whatsappNumber: numberSetting ? numberSetting.setting_value : "",
    };
  }
  return { isMultiple: false, serviceProviders: [], selectedService: null, whatsappNumber: "" };
};

// ─── Helpers: build outlet/user from decoded token claims ────────

const buildOutletFromClaims = (claims: IWhatsAppTokenClaims | null): ICompanyOutlet => {
  if (!claims?.company_outlet_uuid) return defaultCompanyOutlet;
  return {
    ...defaultCompanyOutlet,
    company_outlet_uuid: claims.company_outlet_uuid,
    company_outlet_name: claims.COMPANY_NAME || "",
    logo: claims.LOGO || null,
    company_outlet_timezone: claims.company_outlet_timezone,
    company_outlet_gst_percentage: claims.gst_percentage,
  };
};

const buildUserFromClaims = (claims: IWhatsAppTokenClaims | null): IUserProfile => {
  if (!claims?.user_uuid) return defaultUserProfile;
  const fullName = claims.full_name || "";
  const [firstName, ...rest] = fullName.trim().split(/\s+/);
  return {
    ...defaultUserProfile,
    user_uuid: claims.user_uuid,
    full_name: fullName,
    first_name: firstName || "",
    last_name: rest.join(" "),
    email: claims.email || "",
    role_value: claims.role_value || "",
  };
};

// ─── Provider ────────────────────────────────────────────────────

export const WhatsAppAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<IWhatsAppAuthState>(initialWhatsAppAuthState);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // The auth-Token (JWT) already carries the company outlet + user identity,
      // so we decode it instead of calling /company-outlet/get-company-outlet.
      const token = process.env["REACT_APP_WHATSAPP_API_KEY"] || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJMT0dPIjoiY29tcGFueV9vdXRsZXRfbG9nby9sb2dvXzIwMjYtMDUtMjJfMTAtMDMtMDMuanBlZyIsIkNPTVBBTllfTkFNRSI6IkVkZ2VuTlJvb3RzIFB2dC4gTHRkLiIsImNvbXBhbnlfb3V0bGV0X3V1aWQiOiJhNzliNDRhNi0wZTJhLTQ4NDAtYjE5ZS00ZTIxNjhhYTllMTIiLCJmdWxsX25hbWUiOiJrYW1hbCBBZG1pbiIsInVzZXJfZmFjdF91bmlxdWVfaWQiOjEyNCwidXNlcl91dWlkIjoiNDMwYmNiZjQtYjg1NC00YjFmLWI5NGItNDQwMmQ4ODk2ZGFmIiwiY29tcGFueV9vdXRsZXRfdGltZXpvbmUiOiJBc2lhL2tvbGthdGEiLCJnc3RfcGVyY2VudGFnZSI6MCwiZW1haWwiOiJhZG1pbitrYW1hbEBlZGdlbnJvb3RzLm5ldCIsInJvbGVfdXVpZCI6IjQ4Y2EwNDBlLWVkODYtNGQxYS05YWViLTgwM2RiNDRmNGJmYiIsInJvbGVfdmFsdWUiOiJBRE1JTiIsImJyYW5jaF91dWlkIjpbXSwiaW50ZXJuYWxfZW5yX3VzZXIiOm51bGwsImlhdCI6MTc4MTkwMTE1OCwiZXhwIjoxNzg5Njc3MTU4fQ.ICq03WjtpW2PDq6oZ0cNFJy63taIybyji8ScT8jJyCQ";
      const claims = decodeWhatsAppToken(token);
      console.log("decoded_token", claims);
      const outletData = buildOutletFromClaims(claims);

      // Fetch full user profile + settings in parallel.
      const [userRes, settingsRes] = await Promise.allSettled([
        talkvitApi.get(`/user/get-user?status=ACTIVE&user_uuid=${claims?.user_uuid}`),
        talkvitApi.get("/siteConfiguration/get-user-settings"),
      ]);

      // Prefer the API user profile; fall back to token-derived identity.
      const userData: IUserProfile =
        userRes.status === "fulfilled" && userRes.value.data?.data
          ? userRes.value.data.data[0]
          : buildUserFromClaims(claims);
      const settingsData: any[] =
        settingsRes.status === "fulfilled" ? settingsRes.value.data.data || [] : [];

      setState({
        user: userData,
        outlet: outletData,
        whatsappService: getWhatsappServiceFromSettings(settingsData),
        loading: false,
        error: null,
      });
    } catch (err: any) {
      // Last-resort fallback: still try to build identity from the token.
      const claims = decodeWhatsAppToken(process.env["REACT_APP_WHATSAPP_API_KEY"]);
      setState({
        user: buildUserFromClaims(claims),
        outlet: buildOutletFromClaims(claims),
        whatsappService: initialWhatsAppAuthState.whatsappService,
        loading: false,
        error: err?.response?.data?.message || "Failed to load user profile",
      });
    }
  };

  // Show loading state
  if (state.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show error screen if no user or no outlet configured
  if (!state.user.user_uuid || !state.outlet.company_outlet_uuid) {
    return <NotConfiguredScreen error={state.error} />;
  }

  return (
    <WhatsAppAuthContext.Provider value={state}>
      {children}
    </WhatsAppAuthContext.Provider>
  );
};

// ─── Consumer Hook ───────────────────────────────────────────────

export const useWhatsAppAuth = (): IWhatsAppAuthState => {
  const context = useContext(WhatsAppAuthContext);
  if (!context) {
    throw new Error("useWhatsAppAuth must be used within WhatsAppAuthProvider");
  }
  return context;
};
